import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Checkbox,
  TextField,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import {API_URL,API_UPLOAD_URL} from "./config";

const Teachers = () => {
  const [data, setData] = useState([]);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [empLoading, setEmpLoading] = useState(false);
  const [newRow, setNewRow] = useState({
    CampusCode: "",
    TeacherName: "",
    EmployeeID: null,
    Email: "",
    PhoneNumber: "",
    JoiningDate: "",
    Qualification: "",
    SubjectSpecialization: "",
    IsActive: 1,
  });
  const [editRowId, setEditRowId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [campusCode, setCampusCode] = useState("");
  const fileInputRef = useRef();
  let empFetchCancel;
  const token = localStorage.getItem("auth");
  // 1) Decode campusCode from JWT
  useEffect(() => {
    
    if (!token) return;
    try {
      const decoded = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(decoded.campusCode);
      setNewRow(r => ({ ...r, CampusCode: decoded.campusCode }));
    } catch {
      setError("Failed to decode authentication token");
    }
  }, []);

  // 2) Central fetch function
  const fetchTeachers = async () => {
    if (!campusCode) return;
    try {
      const res = await axios.get(`${API_URL}/api/Teachers`, {
        //params: { CampusCode: campusCode },
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch {
      setError("Failed to fetch teachers");
    }
  };

  // 3) Fetch on mount & whenever campusCode changes
  useEffect(() => {
    fetchTeachers();
  }, [campusCode]);

  // Remote‐search employees
  const fetchEmployees = async query => {
    if (query.length < 3) {
      setEmployeeOptions([]);
      return;
    }
    if (empFetchCancel) empFetchCancel.cancel();
    empFetchCancel = axios.CancelToken.source();
    setEmpLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/Employees/search`, {
        params: {  q: query },
        headers: { Authorization: `Bearer ${token}` },
        cancelToken: empFetchCancel.token,
      }
    );
      setEmployeeOptions(
        res.data.employees.map(e => ({
          id: e.EmployeeID,
          name: e.EmployeeName,
          label: `${e.EmployeeName} (${e.EmployeeID})`,
        }))
      );
    } catch (e) {
      if (!axios.isCancel(e)) console.error(e);
    } finally {
      setEmpLoading(false);
    }
  };

  // Unified handler for inputs
  const handleInputChange = (e, id = null) => {
    const { name, type, value, checked } = e.target;
    const val = type === "checkbox" ? (checked ? 1 : 0) : value;
    if (id == null) {
      setNewRow(r => ({ ...r, [name]: val }));
    } else {
      setData(rows => rows.map(r => (r.id === id ? { ...r, [name]: val } : r)));
    }
  };

  // Toggle active on new row
  const handleNewRowActiveToggle = e => {
    setNewRow(r => ({ ...r, IsActive: e.target.checked ? 1 : 0 }));
  };

  // Add
  const handleAddRow = async () => {
    const prevData = data;
    try {
      await axios.post(`${API_URL}/api/Teachers`, newRow,{headers: { Authorization: `Bearer ${token}` }});
      setSuccess(true);
      setNewRow({
        CampusCode: campusCode,
        TeacherName: "",
        EmployeeID: null,
        Email: "",
        PhoneNumber: "",
        JoiningDate: "",
        Qualification: "",
        SubjectSpecialization: "",
        IsActive: 1,
      });
      await fetchTeachers();
    } catch (err) 
        {
          setError(
            err.response?.status === 409
              ? err.response.data.error
              : err.message || "Failed to add row"
          );
          setData(prevData);
        }
  };

  // Update
  const handleUpdateRow = async id => {
    const updated = data.find(r => r.id === id);
    try {
      await axios.put(`${API_URL}/api/Teachers/${id}`, updated,{headers: { Authorization: `Bearer ${token}` }});
      setSuccess(true);
      setEditRowId(null);
      await fetchTeachers();
    } catch {
      setError("Failed to update teacher");
    }
  };

  // Delete
  const handleDeleteRow = async id => {
    const prevData = data;
    try {
      await axios.delete(`${API_URL}/api/Teachers/${id}`,{headers: { Authorization: `Bearer ${token}` }});
      setSuccess(true);
      await fetchTeachers();
    } catch (err) 
    {
      setError(
        err.response?.status === 409
          ? err.response.data.error
          : "Failed to delete teacher"
      );
      setData(prevData);
    }
  };

  // Bulk upload
  const handleBulkClick = () => fileInputRef.current.click();
  const handleFile = async e => {
    const file = e.target.files[0];
    if (!file) return;
    const form = new FormData();
    form.append("excelFile", file);
    form.append("CampusCode", campusCode);
    try {
      await axios.post(`${API_URL}/api/UploadTeachers`, form, {
        headers: { Authorization: `Bearer ${token}`,"Content-Type": "multipart/form-data" },
      });
      setSuccess(true);
      await fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.error || "Bulk upload failed");
    } finally {
      e.target.value = "";
    }
  };

  return (
    <Paper elevation={5} sx={{ p:2, backgroundColor:"#F2F2F2" }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" style={{fontSize: "1.25rem",fontWeight:500,marginBottom: '10px',alignItems: 'center', display: 'flex' ,color: '#1976D2'}}>Manage Teachers --Maynot be required.</Typography>
        <Button variant="contained" startIcon={<UploadIcon />} onClick={handleBulkClick}>
          Bulk Upload
        </Button>
        <input
          type="file"
          accept=".xlsx,.xls"
          ref={fileInputRef}
          style={{ display:"none" }}
          onChange={handleFile}
        />
      </Box>

      {error && <Alert severity="error" onClose={()=>setError("")}>{error}</Alert>}
      {success && (
        <Snackbar open autoHideDuration={3000} onClose={()=>setSuccess(false)}>
          <Alert severity="success">Operation successful!</Alert>
        </Snackbar>
      )}

      <TableContainer component={Paper}>
      <Table size="small" sx={{ width: "100%" }}>
        <colgroup>
            <col style={{ width: "3%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "5%" }} />
            <col style={{ width: "12%" }} />
          </colgroup>
          <TableHead sx={{ backgroundColor:"#1976D2" }}>
            <TableRow>
              {["#","Name","EmployeeID","Email","Phone","JoinDate","Qualification","Subject","Active","Actions"].map(h => (
                <TableCell key={h} sx={{ color:"white" }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row, idx) => (
              editRowId === row.id ? (
                <TableRow key={row.id} sx={{ bgcolor: idx%2?"white":"#E6F3FF" }}>
                  <TableCell>{idx+1}</TableCell>

                  {/* Name + EmployeeID via Autocomplete */}
                  <TableCell sx={{ minWidth:200 }}>
                    <Autocomplete
                      freeSolo
                      options={employeeOptions}
                      loading={empLoading}
                      onInputChange={(_, q, reason) => reason==="input" && fetchEmployees(q)}
                      getOptionLabel={opt => typeof opt==="string"?opt:opt.name}
                      value={
                        row.EmployeeID
                          ? employeeOptions.find(o=>o.id===row.EmployeeID) || row.TeacherName
                          : row.TeacherName
                      }
                      onChange={(_, nv) => {
                        if (typeof nv==="string") {
                          handleInputChange({ target:{ name:"TeacherName", value:nv } }, row.id);
                          handleInputChange({ target:{ name:"EmployeeID", value:null } }, row.id);
                        } else if (nv && nv.id) {
                          handleInputChange({ target:{ name:"TeacherName", value:nv.name } }, row.id);
                          handleInputChange({ target:{ name:"EmployeeID", value:nv.id } }, row.id);
                        } else {
                          handleInputChange({ target:{ name:"TeacherName", value:"" } }, row.id);
                          handleInputChange({ target:{ name:"EmployeeID", value:null } }, row.id);
                        }
                      }}
                      renderOption={(props, opt) => <li {...props}>{opt.label}</li>}
                      renderInput={params => (
                        <TextField
                          {...params}
                          placeholder="Teacher Name"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {empLoading && <CircularProgress size={20} />}
                                {params.InputProps.endAdornment}
                              </>
                            )
                          }}
                        />
                      )}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField name="EmployeeID" value={row.EmployeeID||""} disabled fullWidth/>
                  </TableCell>

                  {/* Other fields */}
                  <TableCell>
                    <TextField name="Email" value={row.Email} onChange={e=>handleInputChange(e,row.id)} fullWidth/>
                  </TableCell>
                  <TableCell>
                    <TextField name="PhoneNumber" value={row.PhoneNumber} onChange={e=>handleInputChange(e,row.id)} fullWidth/>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      name="JoiningDate"
                      value={row.JoiningDate||""}
                      onChange={e=>handleInputChange(e,row.id)}
                      InputLabelProps={{ shrink:true }}
                      fullWidth
                    />
                  </TableCell>
                  <TableCell>
                    <TextField name="Qualification" value={row.Qualification} onChange={e=>handleInputChange(e,row.id)} fullWidth/>
                  </TableCell>
                  <TableCell>
                    <TextField name="SubjectSpecialization" value={row.SubjectSpecialization} onChange={e=>handleInputChange(e,row.id)} fullWidth/>
                  </TableCell>
                  <TableCell>
                    <Checkbox name="IsActive" checked={!!row.IsActive} onChange={e=>handleInputChange(e,row.id)}/>
                  </TableCell>

                  <TableCell>
                    <IconButton onClick={()=>handleUpdateRow(row.id)}><SaveIcon/></IconButton>
                    <IconButton onClick={()=>setEditRowId(null)}><CancelIcon/></IconButton>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow key={row.id} sx={{ bgcolor: idx%2?"white":"#E6F3FF" }}>
                  <TableCell>{idx+1}</TableCell>
                  <TableCell>{row.TeacherName}</TableCell>
                  <TableCell>{row.EmployeeID}</TableCell>
                  <TableCell>{row.Email}</TableCell>
                  <TableCell>{row.PhoneNumber}</TableCell>
                  <TableCell>{row.JoiningDate?.split("T")[0]}</TableCell>
                  <TableCell>{row.Qualification}</TableCell>
                  <TableCell>{row.SubjectSpecialization}</TableCell>
                  <TableCell><Checkbox checked={!!row.IsActive} readOnly/></TableCell>
                  <TableCell>
                    <IconButton onClick={()=>setEditRowId(row.id)}><EditIcon/></IconButton>
                    <IconButton onClick={()=>handleDeleteRow(row.id)}><DeleteIcon/></IconButton>
                  </TableCell>
                </TableRow>
              )
            ))}

            {/* New Row */}
            <TableRow sx={{ bgcolor: data.length%2?"white":"#E6F3FF" }}>
              <TableCell>{data.length+1}</TableCell>
              <TableCell sx={{ minWidth:200 }}>
                <Autocomplete
                  freeSolo
                  options={employeeOptions}
                  loading={empLoading}
                  onInputChange={(_, q, reason) => reason==="input" && fetchEmployees(q)}
                  getOptionLabel={opt => typeof opt==="string"?opt:opt.name}
                  value={
                    newRow.EmployeeID
                      ? employeeOptions.find(o=>o.id===newRow.EmployeeID) || newRow.TeacherName
                      : newRow.TeacherName
                  }
                  onChange={(_, nv) => {
                    if (typeof nv==="string") {
                      setNewRow(r => ({ ...r, TeacherName:nv, EmployeeID:null }));
                    } else if (nv && nv.id) {
                      setNewRow(r => ({ ...r, TeacherName:nv.name, EmployeeID:nv.id }));
                    } else {
                      setNewRow(r => ({ ...r, TeacherName:"", EmployeeID:null }));
                    }
                  }}
                  renderOption={(props, opt) => <li {...props}>{opt.label}</li>}
                  renderInput={params => (
                    <TextField
                      {...params}
                      placeholder="Teacher Name"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {empLoading && <CircularProgress size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
              </TableCell>
              <TableCell>
                <TextField name="EmployeeID" value={newRow.EmployeeID||""} disabled fullWidth/>
              </TableCell>
              <TableCell>
                <TextField name="Email" value={newRow.Email} onChange={e=>handleInputChange(e)} fullWidth/>
              </TableCell>
              <TableCell>
                <TextField name="PhoneNumber" value={newRow.PhoneNumber} onChange={e=>handleInputChange(e)} fullWidth/>
              </TableCell>
              <TableCell>
                <TextField
                  type="date"
                  name="JoiningDate"
                  value={newRow.JoiningDate}
                  onChange={e=>setNewRow(r=>({...r, JoiningDate:e.target.value}))}
                  InputLabelProps={{ shrink:true }}
                  fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField name="Qualification" value={newRow.Qualification} onChange={e=>handleInputChange(e)} fullWidth/>
              </TableCell>
              <TableCell>
                <TextField name="SubjectSpecialization" value={newRow.SubjectSpecialization} onChange={e=>handleInputChange(e)} fullWidth/>
              </TableCell>
              <TableCell>
                <Checkbox name="IsActive" checked={!!newRow.IsActive} onChange={handleNewRowActiveToggle}/>
              </TableCell>
              <TableCell>
                <IconButton onClick={handleAddRow}><AddIcon/></IconButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default Teachers;
